import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import "../styles/IntakeQCDashboard.css";
import { fetchQCDashboard } from "../api/IntakeQCApi";

// ─────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────
const fmt = (n) =>
  n === null || n === undefined ? "—" : Number(n).toLocaleString();

const fmtPct = (n) =>
  n === null || n === undefined ? "—" : `${Number(n).toFixed(1)}%`;

const fmtDate = (str) => {
  if (!str) return "—";
  return new Date(str).toLocaleString("en-KE", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const statusBadge = (status) => {
  const map = {
    APPROVED:   "badge-approved",
    REJECTED:   "badge-rejected",
    QUARANTINE: "badge-quarantine",
    PENDING:    "badge-pending",
  };
  const cls = map[status] || "badge-pending";
  return (
    <span className={`badge ${cls}`}>
      {status?.charAt(0) + status?.slice(1).toLowerCase()}
    </span>
  );
};

// ─────────────────────────────────────────────
// CSV EXPORT UTILITY
// ─────────────────────────────────────────────
const exportCSV = (filename, rows, headers) => {
  if (!rows || rows.length === 0) return;
  const escape = (v) => {
    if (v === null || v === undefined) return "";
    const s = String(v);
    return s.includes(",") || s.includes('"') || s.includes("\n")
      ? `"${s.replace(/"/g, '""')}"`
      : s;
  };
  const csv = [
    headers.map(escape).join(","),
    ...rows.map((r) => headers.map((h) => escape(r[h])).join(",")),
  ].join("\n");
  const blob = new Blob([csv], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

const exportQCRecordsCSV = (records, filename = "qc_records.csv") => {
  const rows = records.map((r) => ({
    id: r.id,
    intake_id: r.intake,
    status: r.status,
    farm: r.intake_details?.pool?.farm_name,
    ghse: r.intake_details?.pool?.ghse_name,
    variety: r.intake_details?.pool?.variety_name,
    qc_nets: r.qc_nets,
    available_nets: r.available_nets,
    nets_remaining_after_qc: r.nets_remaining_after_qc,
    quarantine_stems: r.quarantine_stems,
    qc_progress_pct: r.qc_progress?.total_nets
      ? Math.round((r.qc_progress.qc_done / r.qc_progress.total_nets) * 100)
      : 0,
    is_fully_qced: r.qc_progress?.is_fully_qced,
    pest_disease_ok: r.pest_disease_ok,
    pest_disease_comment: r.pest_disease_comment,
    cut_stage_ok: r.cut_stage_ok,
    cut_stage_comment: r.cut_stage_comment,
    hooks_ok: r.hooks_ok,
    hooks_comment: r.hooks_comment,
    arrangement_ok: r.arrangement_ok,
    arrangement_comment: r.arrangement_comment,
    is_split_child: r.is_split_child,
    has_split_children: r.has_split_children,
    parent_split: r.parent_split,
    checked_by: r.checked_by,
    checked_at: r.checked_at,
    received_nets: r.intake_details?.received_nets,
    received_stems: r.intake_details?.received_stems,
  }));
  const headers = Object.keys(rows[0]);
  exportCSV(filename, rows, headers);
};

// ─────────────────────────────────────────────
// DATE RANGE UTILITIES
// ─────────────────────────────────────────────
const MONTHS_SHORT = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const DAY_NAMES    = ["Su","Mo","Tu","We","Th","Fr","Sa"];

const todayDate = new Date();
todayDate.setHours(0, 0, 0, 0);

/** Returns a date-only ISO string "YYYY-MM-DD" from a Date object */
// const toISODate = (d) => (d ? d.toISOString().split("T")[0] : undefined);

const toISODate = (d) => {
  if (!d) return undefined;
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/** Build the default last-7-days range */
const getDefault7Days = () => {
  const end   = new Date(todayDate);
  const start = new Date(todayDate);
  start.setDate(start.getDate() - 6);
  return { start, end };
};

const sameDay = (a, b) =>
  a && b &&
  a.getFullYear() === b.getFullYear() &&
  a.getMonth()    === b.getMonth() &&
  a.getDate()     === b.getDate();

const inRange = (d, start, end) => start && end && d >= start && d <= end;

const fmtShort = (d) =>
  d ? `${d.getDate()} ${MONTHS_SHORT[d.getMonth()]} ${d.getFullYear()}` : "—";

const fmtRangeLabel = (start, end) => {
  if (!start && !end)       return "Select range";
  if (start && !end)        return `From ${fmtShort(start)}`;
  if (!start && end)        return `Until ${fmtShort(end)}`;
  if (sameDay(start, end))  return fmtShort(start);
  return `${fmtShort(start)} → ${fmtShort(end)}`;
};

const addMonths = (d, n) => {
  const r = new Date(d);
  r.setDate(1);
  r.setMonth(r.getMonth() + n);
  return r;
};

// No "All time" preset — server must always have a bounded window
const DATE_PRESETS = [
  {
    label: "Today",
    fn: () => ({ start: new Date(todayDate), end: new Date(todayDate) }),
  },
  {
    label: "Yesterday",
    fn: () => {
      const d = new Date(todayDate);
      d.setDate(d.getDate() - 1);
      return { start: d, end: new Date(d) };
    },
  },
  {
    label: "Last 7 days",
    fn: () => {
      const d = new Date(todayDate);
      d.setDate(d.getDate() - 6);
      return { start: d, end: new Date(todayDate) };
    },
  },
  {
    label: "Last 14 days",
    fn: () => {
      const d = new Date(todayDate);
      d.setDate(d.getDate() - 13);
      return { start: d, end: new Date(todayDate) };
    },
  },
  {
    label: "Last 30 days",
    fn: () => {
      const d = new Date(todayDate);
      d.setDate(d.getDate() - 29);
      return { start: d, end: new Date(todayDate) };
    },
  },
  {
    label: "This month",
    fn: () => {
      const d = new Date(todayDate.getFullYear(), todayDate.getMonth(), 1);
      return { start: d, end: new Date(todayDate) };
    },
  },
  {
    label: "Last month",
    fn: () => {
      const s = new Date(todayDate.getFullYear(), todayDate.getMonth() - 1, 1);
      const e = new Date(todayDate.getFullYear(), todayDate.getMonth(), 0);
      return { start: s, end: e };
    },
  },
  {
    label: "This year",
    fn: () => {
      const d = new Date(todayDate.getFullYear(), 0, 1);
      return { start: d, end: new Date(todayDate) };
    },
  },
];

// ─────────────────────────────────────────────
// MINI CALENDAR
// ─────────────────────────────────────────────
const MiniCalendar = ({ viewDate, onNavigate, start, end, hoverDate, onDayClick, onDayHover }) => {
  const year     = viewDate.getFullYear();
  const month    = viewDate.getMonth();
  const firstDay = new Date(year, month, 1).getDay();
  const totalDays= new Date(year, month + 1, 0).getDate();
  const cells    = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= totalDays; d++) cells.push(new Date(year, month, d));

  return (
    <div className="drf-cal">
      <div className="drf-cal-nav">
        <button className="drf-nav-btn" onClick={() => onNavigate(-1)}>‹</button>
        <span className="drf-cal-title">{MONTHS_SHORT[month]} {year}</span>
        <button className="drf-nav-btn" onClick={() => onNavigate(1)}>›</button>
      </div>
      <div className="drf-cal-grid">
        {DAY_NAMES.map((d) => <div key={d} className="drf-day-name">{d}</div>)}
        {cells.map((d, i) => {
          if (!d) return <div key={`e${i}`} className="drf-day-empty" />;
          const isFuture = d > todayDate;
          const isToday  = sameDay(d, todayDate);
          const isStart  = sameDay(d, start);
          const isEnd    = sameDay(d, end || hoverDate);
          const isIn     = inRange(d, start, end || hoverDate);
          const isHov    = !start && sameDay(d, hoverDate);
          let cls = "drf-day";
          if (isFuture) cls += " drf-day--future";
          if (isToday)  cls += " drf-day--today";
          if (isStart)  cls += " drf-day--start";
          if (isEnd)    cls += " drf-day--end";
          if (isIn && !isStart && !isEnd) cls += " drf-day--range";
          if (isHov)    cls += " drf-day--hover";
          return (
            <button
              key={d.getDate()}
              className={cls}
              disabled={isFuture}
              onClick={() => !isFuture && onDayClick(d)}
              onMouseEnter={() => onDayHover(d)}
            >
              {d.getDate()}
            </button>
          );
        })}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────
// DATE RANGE FILTER WIDGET
// ─────────────────────────────────────────────
const DateRangeFilter = ({ value, onChange }) => {
  const [open, setOpen]           = useState(false);
  const [start, setStart]         = useState(value?.start || null);
  const [end,   setEnd]           = useState(value?.end   || null);
  const [hoverDate, setHover]     = useState(null);
  const [view1, setView1]         = useState(
    new Date(todayDate.getFullYear(), todayDate.getMonth() - 1, 1)
  );
  const [view2, setView2]         = useState(
    new Date(todayDate.getFullYear(), todayDate.getMonth(), 1)
  );
  const [activePreset, setActivePreset] = useState("Last 7 days");
  const panelRef = useRef(null);

  // Sync internal state when parent value changes (e.g. initial default)
  useEffect(() => {
    setStart(value?.start || null);
    setEnd(value?.end     || null);
  }, [value]);

  useEffect(() => {
    const handler = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleDayClick = (d) => {
    if (!start || (start && end)) {
      setStart(d); setEnd(null); setActivePreset(null);
    } else {
      const [s, e] = d >= start ? [start, d] : [d, start];
      setStart(s); setEnd(e); setActivePreset(null);
    }
  };

  const applyPreset = (p) => {
    const { start: s, end: e } = p.fn();
    setStart(s); setEnd(e); setActivePreset(p.label);
    onChange?.({ start: s, end: e });
    setOpen(false);
  };

  const handleApply = () => {
    if (start && end) {
      onChange?.({ start, end });
      setOpen(false);
    }
  };

  const diffDays = start && end ? Math.round((end - start) / 86400000) + 1 : 0;
  const hasSelection = start && end;

  return (
    <div className="drf-root" ref={panelRef}>
      <button
        className={`drf-trigger ${open ? "drf-trigger--open" : ""} drf-trigger--active`}
        onClick={() => setOpen((o) => !o)}
      >
        <span className="drf-trigger-icon">
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <rect x="1" y="2" width="12" height="11" rx="2" stroke="currentColor" strokeWidth="1.3"/>
            <path d="M1 6h12" stroke="currentColor" strokeWidth="1.3"/>
            <path d="M4 1v2M10 1v2" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
            <circle cx="4.5" cy="9" r="0.8" fill="currentColor"/>
            <circle cx="7"   cy="9" r="0.8" fill="currentColor"/>
            <circle cx="9.5" cy="9" r="0.8" fill="currentColor"/>
          </svg>
        </span>
        <span className="drf-trigger-label">{fmtRangeLabel(start, end)}</span>
        <span className="drf-trigger-arrow">{open ? "▲" : "▼"}</span>
      </button>

      {open && (
        <div className="drf-panel">
          {/* Preset sidebar */}
          <div className="drf-presets">
            <div className="drf-presets-label">Quick Select</div>
            {DATE_PRESETS.map((p) => (
              <button
                key={p.label}
                className={`drf-preset ${activePreset === p.label ? "drf-preset--active" : ""}`}
                onClick={() => applyPreset(p)}
              >
                {p.label}
              </button>
            ))}
          </div>

          {/* Calendar + footer */}
          <div className="drf-main">
            {/* Step hint / timeline */}
            <div className="drf-hint-row">
              {!start ? (
                <span className="drf-hint">Click to set <strong>start date</strong></span>
              ) : !end ? (
                <span className="drf-hint">Now pick an <strong>end date</strong></span>
              ) : (
                <div className="drf-timeline-wrap">
                  <span className="drf-timeline-from">{fmtShort(start)}</span>
                  <div className="drf-timeline-rail">
                    {Array.from({ length: Math.min(diffDays, 32) }).map((_, i) => (
                      <div key={i} className="drf-timeline-tick" style={{ animationDelay: `${i * 18}ms` }} />
                    ))}
                  </div>
                  <span className="drf-timeline-badge">{diffDays}d</span>
                  <div className="drf-timeline-rail">
                    {Array.from({ length: Math.min(diffDays, 32) }).map((_, i) => (
                      <div
                        key={i}
                        className="drf-timeline-tick drf-timeline-tick--right"
                        style={{ animationDelay: `${(Math.min(diffDays, 32) - i) * 18}ms` }}
                      />
                    ))}
                  </div>
                  <span className="drf-timeline-to">{fmtShort(end)}</span>
                </div>
              )}
            </div>

            {/* Two calendars */}
            <div className="drf-calendars">
              <MiniCalendar
                viewDate={view1}
                onNavigate={(n) => setView1((v) => addMonths(v, n))}
                start={start} end={end} hoverDate={hoverDate}
                onDayClick={handleDayClick}
                onDayHover={setHover}
              />
              <div className="drf-cal-sep" />
              <MiniCalendar
                viewDate={view2}
                onNavigate={(n) => {
                  setView2((v) => {
                    const next = addMonths(v, n);
                    if (n < 0 && next <= view1) return v;
                    return next;
                  });
                }}
                start={start} end={end} hoverDate={hoverDate}
                onDayClick={handleDayClick}
                onDayHover={setHover}
              />
            </div>

            {/* Footer */}
            <div className="drf-footer">
              <span className="drf-footer-summary">
                {hasSelection ? fmtRangeLabel(start, end) : "Pick a start and end date"}
              </span>
              <div className="drf-footer-btns">
                <button
                  className="drf-btn-apply"
                  disabled={!hasSelection}
                  onClick={handleApply}
                >
                  Apply
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────
// WORD CLOUD + COMMENTS ANALYTICS
// ─────────────────────────────────────────────
const STOP_WORDS = new Set([
  "the","a","an","and","or","but","in","on","at","to","for","of","with","is",
  "was","are","were","be","been","has","have","had","it","its","this","that",
  "i","we","you","they","no","not","very","some","as","by","from","so","do",
  "did","will","can","all","if","my","our","us","he","she","we're","i'm",
  "its","been","also","more","any","there","their","them","then","when","what",
  "which","who","how","where","why","each","few","both","too","just","could",
  "would","should","may","might","must","shall","upon","into","onto","over",
  "under","out","up","down","off","around","after","before","about","through",
]);

const CHECK_FIELDS = [
  { key: "pest_disease_comment", label: "Pest & Disease", short: "P&D", icon: "🌿", color: "#40b074", bg: "#d6f0e2" },
  { key: "cut_stage_comment",    label: "Cut Stage",      short: "Cut", icon: "✂️", color: "#a92f6f", bg: "#f5dcea" },
  { key: "hooks_comment",        label: "Hooks",          short: "Hks", icon: "🪝", color: "#e89020", bg: "#fef1d6" },
  { key: "arrangement_comment",  label: "Arrangement",    short: "Arr", icon: "📐", color: "#4a5a9a", bg: "#e4e8f2" },
];

const WORD_COLORS = [
  { minScore: 0.78, color: "#a92f6f", bg: "#fae8f3", weight: 800 },
  { minScore: 0.55, color: "#7d1f52", bg: "#f5dcea", weight: 700 },
  { minScore: 0.38, color: "#a9ae50", bg: "#eef0d4", weight: 600 },
  { minScore: 0.22, color: "#7a8030", bg: "#f4f5e6", weight: 500 },
  { minScore: 0,    color: "#8a7e74", bg: "#f7f5f2", weight: 400 },
];

const buildWordFreq = (records, fieldKeys) => {
  const freq = {};
  records.forEach((r) => {
    fieldKeys.forEach((f) => {
      const txt = r[f];
      if (!txt) return;
      txt.toLowerCase().replace(/[^a-z0-9\s'-]/g, " ").split(/\s+/).forEach((w) => {
        const clean = w.replace(/^['-]+|['-]+$/g, "");
        if (clean.length >= 3 && !STOP_WORDS.has(clean)) {
          freq[clean] = (freq[clean] || 0) + 1;
        }
      });
    });
  });
  const sorted = Object.entries(freq)
    .map(([word, count]) => ({ word, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 60);
  const max = sorted[0]?.count || 1;
  return sorted.map((w) => ({ ...w, score: w.count / max }));
};

const highlightWord = (text, word) => {
  const re = new RegExp(`(${word.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`, "gi");
  return text.split(re).map((part, i) =>
    re.test(part) ? <mark key={i} className="cwc-mark">{part}</mark> : part
  );
};

const CommentsTab = ({ qcRecords }) => {
  const [activeCategory, setActiveCategory] = useState("all");
  const [selectedWord, setSelectedWord]     = useState(null);
  const [sortMode, setSortMode]             = useState("freq");
  const [viewMode, setViewMode]             = useState("cloud");

  const activeFields = useMemo(() =>
    activeCategory === "all"
      ? CHECK_FIELDS.map((f) => f.key)
      : [CHECK_FIELDS.find((f) => f.key === activeCategory)?.key].filter(Boolean),
    [activeCategory]
  );

  const words = useMemo(() => buildWordFreq(qcRecords, activeFields), [qcRecords, activeFields]);

  const displayWords = useMemo(() => {
    const w = [...words];
    if (sortMode === "alpha") w.sort((a, b) => a.word.localeCompare(b.word));
    return w;
  }, [words, sortMode]);

  const fieldStats = useMemo(() =>
    CHECK_FIELDS.map((f) => {
      const withComment    = qcRecords.filter((r) => r[f.key]?.trim()).length;
      const withoutComment = qcRecords.length - withComment;
      const failWithComment= qcRecords.filter(
        (r) => r[f.key.replace("_comment", "_ok")] === false && r[f.key]?.trim()
      ).length;
      return { ...f, withComment, withoutComment, failWithComment, total: qcRecords.length };
    }),
    [qcRecords]
  );

  const mentions = useMemo(() => {
    if (!selectedWord) return [];
    const results = [];
    qcRecords.forEach((r) => {
      CHECK_FIELDS.forEach((f) => {
        const txt = r[f.key];
        if (txt && txt.toLowerCase().includes(selectedWord.toLowerCase())) {
          results.push({ ...r, _field: f, _text: txt });
        }
      });
    });
    return results;
  }, [selectedWord, qcRecords]);

  const allComments = useMemo(() => {
    const out = [];
    qcRecords.forEach((r) => {
      CHECK_FIELDS.forEach((f) => {
        const txt = r[f.key];
        if (txt?.trim()) out.push({
          id: r.id, status: r.status,
          farm: r.intake_details?.pool?.farm_name,
          field: f, text: txt,
          ok: r[f.key.replace("_comment", "_ok")],
        });
      });
    });
    return out;
  }, [qcRecords]);

  const totalComments = allComments.length;

  return (
    <div className="comments-tab">
      <div className="cwc-stats-row">
        {fieldStats.map((f) => (
          <div className="cwc-stat-card" key={f.key}>
            <div className="cwc-stat-icon" style={{ background: f.bg, color: f.color }}>{f.icon}</div>
            <div className="cwc-stat-body">
              <div className="cwc-stat-label">{f.label}</div>
              <div className="cwc-stat-val" style={{ color: f.color }}>{f.withComment}</div>
              <div className="cwc-stat-sub">of {f.total} checked</div>
              <div className="cwc-stat-bar-wrap">
                <div
                  className="cwc-stat-bar-fill"
                  style={{
                    width: `${f.total > 0 ? (f.withComment / f.total) * 100 : 0}%`,
                    background: f.color,
                  }}
                />
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="cwc-card" style={{ marginTop: 20 }}>
        <div className="cwc-card-header">
          <div className="cwc-card-title">
            <span className="cwc-cloud-icon">☁</span>
            <span>Comment Word Cloud</span>
            <span className="cwc-word-count-badge">{words.length} terms</span>
          </div>
          <div className="cwc-card-controls">
            <div className="cwc-sort-pill">
              <button className={sortMode === "freq"  ? "active" : ""} onClick={() => setSortMode("freq")}>Freq</button>
              <button className={sortMode === "alpha" ? "active" : ""} onClick={() => setSortMode("alpha")}>A–Z</button>
            </div>
            <div className="cwc-sort-pill">
              <button className={viewMode === "cloud" ? "active" : ""} onClick={() => setViewMode("cloud")}>☁ Cloud</button>
              <button className={viewMode === "list"  ? "active" : ""} onClick={() => setViewMode("list")}>☰ List</button>
            </div>
          </div>
        </div>
        <div className="cwc-cat-tabs">
          <button
            className={`cwc-cat-tab ${activeCategory === "all" ? "active" : ""}`}
            onClick={() => { setActiveCategory("all"); setSelectedWord(null); }}
          >
            💬 All
          </button>
          {CHECK_FIELDS.map((f) => (
            <button
              key={f.key}
              className={`cwc-cat-tab ${activeCategory === f.key ? "active" : ""}`}
              style={activeCategory === f.key ? { color: f.color, borderColor: f.color } : {}}
              onClick={() => { setActiveCategory(f.key); setSelectedWord(null); }}
            >
              {f.icon} {f.short}
            </button>
          ))}
        </div>

        {words.length === 0 ? (
          <div className="cwc-empty"><span>💬</span><p>No comments in this category</p></div>
        ) : (
          <div className={`cwc-body cwc-body--${viewMode}`}>
            {displayWords.map(({ word, count, score }, i) => {
              const bucket   = WORD_COLORS.find((b) => score >= b.minScore) || WORD_COLORS[WORD_COLORS.length - 1];
              const fontSize = viewMode === "cloud" ? 0.7 + score * 1.15 : 0.82;
              const isSelected = selectedWord === word;
              return (
                <button
                  key={word}
                  className={`cwc-word ${isSelected ? "cwc-word--selected" : ""} ${viewMode === "list" ? "cwc-word--list" : ""}`}
                  style={{
                    fontSize:   `${fontSize}rem`,
                    color:      isSelected ? "#fff"       : bucket.color,
                    background: isSelected ? bucket.color : bucket.bg,
                    border:     `1px solid ${isSelected ? bucket.color : "transparent"}`,
                    fontWeight: bucket.weight,
                    animationDelay: viewMode === "cloud" ? `${i * 16}ms` : "0ms",
                  }}
                  onClick={() => setSelectedWord((s) => s === word ? null : word)}
                  title={`"${word}" — ${count} occurrence${count !== 1 ? "s" : ""}`}
                >
                  {word}
                  {viewMode === "list" && (
                    <span className="cwc-word-freq" style={{ color: isSelected ? "rgba(255,255,255,0.7)" : bucket.color }}>
                      {count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        )}

        <div className="cwc-legend">
          <span className="cwc-legend-dot" style={{ background: "#a92f6f" }} /> Most frequent
          <span className="cwc-legend-dot" style={{ background: "#a9ae50", marginLeft: 12 }} /> Moderate
          <span className="cwc-legend-dot" style={{ background: "#8a7e74", marginLeft: 12 }} /> Rare
          <span style={{ marginLeft: "auto", fontSize: "0.72rem", color: "var(--text-muted)", fontStyle: "italic" }}>
            Click any word to drill in
          </span>
        </div>
      </div>

      {selectedWord && (
        <div className="cwc-drill-panel">
          <div className="cwc-drill-header">
            <span className="cwc-drill-word">"{selectedWord}"</span>
            <span className="cwc-drill-count">{mentions.length} mention{mentions.length !== 1 ? "s" : ""}</span>
            <button className="cwc-drill-close" onClick={() => setSelectedWord(null)}>✕</button>
          </div>
          <div className="cwc-drill-list">
            {mentions.slice(0, 10).map((m, i) => (
              <div key={i} className="cwc-drill-item" style={{ borderLeftColor: m._field.color }}>
                <div className="cwc-drill-meta">
                  <span className="cwc-drill-id">#{m.id}</span>
                  {m.farm && <span className="cwc-drill-farm">{m.farm}</span>}
                  {statusBadge(m.status)}
                  <span className="cwc-drill-field" style={{ color: m._field.color, background: m._field.bg }}>
                    {m._field.icon} {m._field.short}
                  </span>
                  <span className={`cwc-drill-result ${m.ok ? "pass" : "fail"}`}>
                    {m.ok ? "✓ Pass" : "✗ Fail"}
                  </span>
                </div>
                <p className="cwc-drill-text">{highlightWord(m._text, selectedWord)}</p>
              </div>
            ))}
            {mentions.length > 10 && (
              <div className="cwc-drill-more">+{mentions.length - 10} more records contain this word</div>
            )}
          </div>
        </div>
      )}

      <div className="cwc-analytics-grid" style={{ marginTop: 20 }}>
        <div className="cwc-card">
          <div className="cwc-card-header">
            <div className="cwc-card-title"><span>📝</span> Fails with Comments</div>
          </div>
          <div className="cwc-analytics-body">
            {fieldStats.map((f) => {
              const pct = f.withComment > 0 ? Math.round((f.failWithComment / f.withComment) * 100) : 0;
              return (
                <div key={f.key} className="cwc-analytics-row">
                  <span className="cwc-analytics-icon">{f.icon}</span>
                  <span className="cwc-analytics-label">{f.short}</span>
                  <div className="cwc-analytics-bar-wrap">
                    <div className="cwc-analytics-bar-fill" style={{ width: `${pct}%`, background: f.color }} />
                  </div>
                  <span className="cwc-analytics-num" style={{ color: f.color }}>
                    {f.failWithComment}<span style={{ color: "var(--text-muted)", fontWeight: 400 }}>/{f.withComment}</span>
                  </span>
                </div>
              );
            })}
            <p className="cwc-analytics-footnote">Fail records that included a comment</p>
          </div>
        </div>

        <div className="cwc-card">
          <div className="cwc-card-header">
            <div className="cwc-card-title"><span>🎯</span> Comment Coverage</div>
          </div>
          <div className="cwc-analytics-body">
            {fieldStats.map((f) => {
              const pct = f.total > 0 ? Math.round((f.withComment / f.total) * 100) : 0;
              return (
                <div key={f.key} className="cwc-coverage-row">
                  <div className="cwc-coverage-ring-wrap">
                    <svg width="44" height="44" viewBox="0 0 44 44">
                      <circle cx="22" cy="22" r="16" fill="none" stroke="var(--border-light)" strokeWidth="5" />
                      <circle
                        cx="22" cy="22" r="16"
                        fill="none"
                        stroke={f.color}
                        strokeWidth="5"
                        strokeDasharray={`${(pct / 100) * 100.53} 100.53`}
                        strokeLinecap="round"
                        transform="rotate(-90 22 22)"
                        style={{ transition: "stroke-dasharray 0.8s ease" }}
                      />
                      <text x="22" y="26" textAnchor="middle" fontSize="9" fontWeight="700" fill={f.color} fontFamily="Sora, sans-serif">
                        {pct}%
                      </text>
                    </svg>
                  </div>
                  <div className="cwc-coverage-info">
                    <span className="cwc-coverage-name">{f.icon} {f.label}</span>
                    <span className="cwc-coverage-sub">{f.withComment} of {f.total} have comments</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="cwc-card cwc-card--wide">
          <div className="cwc-card-header">
            <div className="cwc-card-title"><span>💬</span> Recent Comments</div>
            <span className="cwc-total-badge">{totalComments} total</span>
          </div>
          <div className="cwc-comments-feed">
            {allComments.slice(0, 8).map((c, i) => (
              <div key={i} className="cwc-comment-item" style={{ borderLeftColor: c.field.color }}>
                <div className="cwc-comment-meta">
                  <span className="cwc-comment-id">#{c.id}</span>
                  {c.farm && <span className="cwc-comment-farm">{c.farm}</span>}
                  {statusBadge(c.status)}
                  <span className="cwc-comment-field" style={{ color: c.field.color, background: c.field.bg }}>
                    {c.field.icon} {c.field.short}
                  </span>
                  <span className={`cwc-comment-result ${c.ok ? "pass" : "fail"}`}>
                    {c.ok ? "✓" : "✗"}
                  </span>
                </div>
                <p className="cwc-comment-text">"{c.text}"</p>
              </div>
            ))}
            {allComments.length === 0 && (
              <div className="cwc-empty"><span>💬</span><p>No comments recorded yet</p></div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────
// DONUT CHART
// ─────────────────────────────────────────────
const DonutChart = ({ data, size = 120, thickness = 22 }) => {
  const r    = (size - thickness) / 2;
  const cx   = size / 2;
  const cy   = size / 2;
  const circ = 2 * Math.PI * r;
  const total= data.reduce((s, d) => s + d.value, 0);
  let offset = 0;

  return (
    <div className="donut-wrap">
      <svg
        className="donut-svg"
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        style={{ transform: "rotate(-90deg)" }}
      >
        {total === 0 ? (
          <circle cx={cx} cy={cy} r={r} fill="none" stroke="#e0dbd2" strokeWidth={thickness} />
        ) : (
          data.map((seg, i) => {
            const pct  = seg.value / total;
            const dash = pct * circ;
            const gap  = circ - dash;
            const el   = (
              <circle
                key={i}
                cx={cx} cy={cy} r={r}
                fill="none"
                stroke={seg.color}
                strokeWidth={thickness}
                strokeDasharray={`${dash} ${gap}`}
                strokeDashoffset={-offset}
                strokeLinecap="butt"
              />
            );
            offset += dash;
            return el;
          })
        )}
        <text
          x={cx} y={cy}
          textAnchor="middle"
          dominantBaseline="central"
          style={{
            fill: "#1c1410", fontSize: "1rem", fontWeight: 800,
            fontFamily: "Sora, sans-serif",
            transform: "rotate(90deg)",
            transformOrigin: `${cx}px ${cy}px`,
          }}
        >
          {total}
        </text>
      </svg>
      <div className="donut-legend">
        {data.map((seg, i) => (
          <div className="donut-legend-item" key={i}>
            <span className="donut-legend-dot" style={{ background: seg.color }} />
            <span className="donut-legend-label">{seg.label}</span>
            <span className="donut-legend-count">
              {seg.value}
              <span style={{ color: "#8a7e74", marginLeft: 4 }}>
                ({total ? ((seg.value / total) * 100).toFixed(0) : 0}%)
              </span>
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────
// CHECK DETAIL CARD
// ─────────────────────────────────────────────
const CheckDetailPanel = ({ rec }) => {
  const checks = [
    { key: "Pest & Disease", ok: rec.pest_disease_ok, comment: rec.pest_disease_comment, icon: "🌿" },
    { key: "Cut Stage",      ok: rec.cut_stage_ok,    comment: rec.cut_stage_comment,    icon: "✂️" },
    { key: "Hooks",          ok: rec.hooks_ok,         comment: rec.hooks_comment,         icon: "🪝" },
    { key: "Arrangement",    ok: rec.arrangement_ok,  comment: rec.arrangement_comment,  icon: "📐" },
  ];
  return (
    <div className="checks-detail-grid">
      {checks.map((c) => (
        <div className="check-card" key={c.key}>
          <div className="check-card-header">
            <span className="check-card-title">{c.icon} {c.key}</span>
            <span className={`check-result ${c.ok ? "pass" : "fail"}`}>{c.ok ? "✓ Pass" : "✗ Fail"}</span>
          </div>
          {c.comment
            ? <p className="check-comment">"{c.comment}"</p>
            : <p className="check-no-comment">No comment</p>}
        </div>
      ))}
    </div>
  );
};

// ─────────────────────────────────────────────
// SPLIT TREE DISPLAY
// ─────────────────────────────────────────────
const SplitTreeDisplay = ({ splitHistory }) => {
  if (!splitHistory) return null;
  return (
    <div className="split-tree-visual">
      <div className="split-node">
        <span className="split-connector">●</span>
        <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "0.78rem" }}>#{splitHistory.id}</span>
        {statusBadge(splitHistory.status)}
        <span style={{ fontSize: "0.75rem", color: "#8a7e74" }}>{splitHistory.qc_nets} nets</span>
        <span style={{ fontSize: "0.72rem", color: "#8a7e74", marginLeft: "auto" }}>{fmtDate(splitHistory.checked_at)}</span>
      </div>
      {(splitHistory.children || []).map((child) => (
        <div className="split-node child" key={child.id}>
          <span className="split-connector">↳</span>
          <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "0.78rem" }}>#{child.id}</span>
          {statusBadge(child.status)}
          <span style={{ fontSize: "0.75rem", color: "#8a7e74" }}>{child.qc_nets} nets</span>
          <span style={{ fontSize: "0.72rem", color: "#8a7e74", marginLeft: "auto" }}>{fmtDate(child.checked_at)}</span>
        </div>
      ))}
    </div>
  );
};

// ─────────────────────────────────────────────
// QC RECORD ROW
// ─────────────────────────────────────────────
const QCRecordRow = ({ rec }) => {
  const [open, setOpen]           = useState(false);
  const [detailTab, setDetailTab] = useState("info");
  const prog     = rec.qc_progress || {};
  const pct      = prog.total_nets > 0 ? Math.round((prog.qc_done / prog.total_nets) * 100) : 0;
  const checks   = [
    { key: "P&D", ok: rec.pest_disease_ok },
    { key: "CUT", ok: rec.cut_stage_ok },
    { key: "HKS", ok: rec.hooks_ok },
    { key: "ARR", ok: rec.arrangement_ok },
  ];
  const passCount = checks.filter((c) => c.ok).length;

  return (
    <>
      <tr>
        <td className="td-mono" style={{ color: "#a92f6f", fontWeight: 600 }}>#{rec.id}</td>
        <td>{statusBadge(rec.status)}</td>
        <td style={{ fontWeight: 500 }}>{rec.intake_details?.pool?.farm_name || "—"}</td>
        <td><span className="pool-tag">{rec.intake_details?.pool?.variety_name || "—"}</span></td>
        <td className="td-mono">{fmt(rec.qc_nets)}</td>
        <td className="td-mono">{fmt(rec.available_nets)}</td>
        <td>
          <div className="qc-progress-mini">
            <div className="bar"><div className="bar-fill" style={{ width: `${pct}%` }} /></div>
            <span className="pct">{pct}%</span>
            {prog.is_fully_qced && <span style={{ fontSize: "0.68rem", color: "#2d7d4f", fontWeight: 700 }}>✓</span>}
          </div>
        </td>
        <td>
          <div className="checks-row">
            {checks.map((c) => (
              <span key={c.key} className={`check-pill ${c.ok ? "check-pass" : "check-fail"}`}>{c.key}</span>
            ))}
          </div>
        </td>
        <td>
          <span style={{ fontSize: "0.73rem", fontWeight: 700, color: passCount === 4 ? "#2d7d4f" : passCount >= 2 ? "#a96b10" : "#b33030" }}>
            {passCount}/4
          </span>
        </td>
        <td>
          {rec.is_split_child   && <span className="split-indicator">↳ child</span>}
          {rec.has_split_children && <span className="split-indicator">⤵ {rec.split_history?.children?.length || 0}</span>}
          {!rec.is_split_child && !rec.has_split_children && <span style={{ color: "#c8c0b5", fontSize: "0.75rem" }}>—</span>}
        </td>
        <td>{fmtDate(rec.checked_at)}</td>
        <td>
          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
            <button className="record-toggle" onClick={() => setOpen((o) => !o)}>{open ? "▲" : "▼"}</button>
            <button className="btn-csv" title="Export" onClick={() => exportQCRecordsCSV([rec], `qc_record_${rec.id}.csv`)}>↓</button>
          </div>
        </td>
      </tr>
      {open && (
        <tr>
          <td colSpan={12} style={{ padding: 0 }}>
            <div className="record-detail">
              <div className="detail-tabs">
                {[
                  { key: "info",   label: "📋 Info" },
                  { key: "checks", label: "✅ QC Checks" },
                  { key: "split",  label: "🔀 Split History" },
                ].map((t) => (
                  <button
                    key={t.key}
                    className={`detail-tab ${detailTab === t.key ? "active" : ""}`}
                    onClick={() => setDetailTab(t.key)}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
              {detailTab === "info" && (
                <div className="record-detail-grid">
                  {[
                    ["Intake ID",           `#${rec.intake}`],
                    ["GHSE",               rec.intake_details?.pool?.ghse_name || "—"],
                    ["Received Nets",      fmt(rec.intake_details?.received_nets)],
                    ["Received Stems",     fmt(rec.intake_details?.received_stems)],
                    ["Remaining After QC", fmt(rec.nets_remaining_after_qc)],
                    ["Quarantine Stems",   fmt(rec.quarantine_stems)],
                    ["Total Nets",         fmt(prog.total_nets)],
                    ["QC Done",            fmt(prog.qc_done)],
                    ["Remaining to QC",    fmt(prog.remaining_to_qc)],
                    ["Checked By",         `User #${rec.checked_by}`],
                    ["Checked At",         fmtDate(rec.checked_at)],
                    ["Parent Split",       rec.parent_split ? `#${rec.parent_split}` : "—"],
                  ].map(([k, v]) => (
                    <div className="detail-item" key={k}>
                      <div className="detail-key">{k}</div>
                      <div className="detail-val">{v}</div>
                    </div>
                  ))}
                </div>
              )}
              {detailTab === "checks" && <CheckDetailPanel rec={rec} />}
              {detailTab === "split"  && <SplitTreeDisplay splitHistory={rec.split_history} />}
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

// ─────────────────────────────────────────────
// POOL SNAPSHOT TABLE
// ─────────────────────────────────────────────
const PoolSnapshotTable = ({ data }) => {
  const unique = Object.values(
    (data || []).reduce((acc, row) => {
      const id = row["intake__pool__id"];
      if (!acc[id]) acc[id] = row;
      return acc;
    }, {})
  );
  if (!unique.length) return <div className="empty-state"><span className="icon">🪴</span>No pool data</div>;
  return (
    <>
      <div className="table-toolbar">
        <span className="results-count"><strong>{unique.length}</strong> unique pools</span>
        <button
          className="btn-csv"
          onClick={() => exportCSV("pool_snapshot.csv", unique.map((r) => ({
            pool_id:         r["intake__pool__id"],
            date:            r["intake__pool__date"],
            farm:            r["intake__pool__ghse__farm__name"],
            ghse:            r["intake__pool__ghse__name"],
            variety:         r["intake__pool__variety__name"],
            remaining_nets:  r["intake__pool__remaining_nets"],
            remaining_stems: r["intake__pool__remaining_stems"],
          })), ["pool_id","date","farm","ghse","variety","remaining_nets","remaining_stems"])}
        >
          ↓ Export CSV
        </button>
      </div>
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Pool ID</th><th>Date</th><th>Farm</th><th>GHSE</th>
              <th>Variety</th><th>Remaining Nets</th><th>Remaining Stems</th>
            </tr>
          </thead>
          <tbody>
            {unique.map((row) => (
              <tr key={row["intake__pool__id"]}>
                <td className="td-mono" style={{ color: "#a92f6f", fontWeight: 600 }}>#{row["intake__pool__id"]}</td>
                <td>{row["intake__pool__date"]}</td>
                <td>{row["intake__pool__ghse__farm__name"]}</td>
                <td>{row["intake__pool__ghse__name"]}</td>
                <td><span className="pool-tag">{row["intake__pool__variety__name"]}</span></td>
                <td className="td-mono">{fmt(row["intake__pool__remaining_nets"])}</td>
                <td className="td-mono">{fmt(row["intake__pool__remaining_stems"])}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
};

// ─────────────────────────────────────────────
// INTAKE SNAPSHOT TABLE
// ─────────────────────────────────────────────
const IntakeSnapshotTable = ({ data }) => {
  const [search, setSearch] = useState("");
  const unique = Object.values(
    (data || []).reduce((acc, row) => {
      const id = row["intake__id"];
      if (!acc[id]) acc[id] = row;
      return acc;
    }, {})
  );
  const filtered = unique.filter((r) => search ? String(r["intake__id"]).includes(search) : true);
  if (!unique.length) return <div className="empty-state"><span className="icon">📦</span>No intake records</div>;
  return (
    <>
      <div className="table-toolbar">
        <div className="toolbar-left">
          <input
            type="text" className="filter-input" placeholder="Search intake ID…"
            value={search} onChange={(e) => setSearch(e.target.value)} style={{ width: 160 }}
          />
          <span className="results-count"><strong>{filtered.length}</strong> of {unique.length}</span>
        </div>
        <button
          className="btn-csv"
          onClick={() => exportCSV("intake_snapshot.csv", filtered.map((r) => ({
            intake_id:          r["intake__id"],
            received_at:        r["intake__received_at"],
            received_nets:      r["intake__received_nets"],
            received_stems:     r["intake__received_stems"],
            nets_balance_after: r["intake__nets_balance_after"],
            stems_balance_after:r["intake__stems_balance_after"],
          })), ["intake_id","received_at","received_nets","received_stems","nets_balance_after","stems_balance_after"])}
        >
          ↓ Export CSV
        </button>
      </div>
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Intake ID</th><th>Received At</th><th>Nets Received</th>
              <th>Stems Received</th><th>Nets Balance After</th><th>Stems Balance After</th>
            </tr>
          </thead>
          <tbody>
            {filtered.slice(0, 30).map((row) => (
              <tr key={row["intake__id"]}>
                <td className="td-mono" style={{ color: "#a92f6f", fontWeight: 600 }}>#{row["intake__id"]}</td>
                <td>{fmtDate(row["intake__received_at"])}</td>
                <td className="td-mono">{fmt(row["intake__received_nets"])}</td>
                <td className="td-mono">{fmt(row["intake__received_stems"])}</td>
                <td className="td-mono">{fmt(row["intake__nets_balance_after"])}</td>
                <td className="td-mono">{fmt(row["intake__stems_balance_after"])}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length > 30 && (
          <div style={{ padding: "10px 16px", fontSize: "0.78rem", color: "#8a7e74" }}>
            Showing 30 of {filtered.length} records
          </div>
        )}
      </div>
    </>
  );
};

// ─────────────────────────────────────────────
// ANALYTICS TAB
// ─────────────────────────────────────────────
const AnalyticsTab = ({ data }) => {
  const counts         = data.counts          || {};
  const rates          = data.rates           || {};
  const statusBreakdown= data.status_breakdown || [];
  const farmBreakdown  = data.farm_breakdown   || [];
  const donutData = [
    { label: "Approved",   value: counts.approved   || 0, color: "#40b074" },
    { label: "Rejected",   value: counts.rejected   || 0, color: "#d95050" },
    { label: "Quarantine", value: counts.quarantine || 0, color: "#e89020" },
  ];
  const maxStatusCount = Math.max(...statusBreakdown.map((s) => s.count), 1);

  return (
    <>
      <div className="twin-col" style={{ marginTop: 24 }}>
        <div className="card">
          <div className="card-header">
            <h3>Status Distribution</h3>
            <button className="btn-csv" onClick={() => exportCSV("status_breakdown.csv", statusBreakdown, ["status","count","nets"])}>↓ CSV</button>
          </div>
          <div className="card-body">
            <div className="status-chart">
              {statusBreakdown.map((s, i) => {
                const colors = {
                  APPROVED:   { fill: "#d6f0e2", bar: "#40b074" },
                  REJECTED:   { fill: "#fce8e8", bar: "#d95050" },
                  QUARANTINE: { fill: "#fef1d6", bar: "#e89020" },
                };
                const c   = colors[s.status] || { fill: "#e4e8f2", bar: "#a92f6f" };
                const pct = Math.round((s.count / maxStatusCount) * 100);
                return (
                  <div className="status-row" key={i}>
                    <span className="status-dot" style={{ background: c.bar }} />
                    <span className="status-name">{s.status.charAt(0) + s.status.slice(1).toLowerCase()}</span>
                    <div className="status-bar-wrap">
                      <div className="status-bar-fill" style={{ width: `${pct}%`, background: c.fill, borderRight: `3px solid ${c.bar}` }}>
                        <span className="status-bar-text">{s.nets} nets</span>
                      </div>
                    </div>
                    <span className="status-count">{s.count}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        <div className="card">
          <div className="card-header"><h3>QC Breakdown</h3><span style={{ fontSize: "0.73rem", color: "#8a7e74" }}>by record count</span></div>
          <div className="card-body">
            <DonutChart data={donutData} size={130} thickness={24} />
            <div style={{ marginTop: 20 }}>
              <div className="rates-list">
                {[
                  { label: "Approval Rate",   val: rates.approvalRate,   cls: "green" },
                  { label: "Rejection Rate",  val: rates.rejectionRate,  cls: "red"   },
                  { label: "Quarantine Rate", val: rates.quarantineRate, cls: "amber" },
                ].map((r) => (
                  <div className="rate-row" key={r.label}>
                    <div className="rate-info">
                      <span className="rate-label">{r.label}</span>
                      <span className="rate-value">{fmtPct(r.val)}</span>
                    </div>
                    <div className="rate-track">
                      <div className={`rate-fill ${r.cls}`} style={{ width: `${r.val || 0}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {farmBreakdown.length > 0 && (
        <>
          <div className="section-label">
            <h2>Farm Breakdown</h2>
            <span className="pill olive">{farmBreakdown.length} farm{farmBreakdown.length !== 1 ? "s" : ""}</span>
            <div className="section-actions">
              <button
                className="btn-csv"
                onClick={() => exportCSV("farm_breakdown.csv", farmBreakdown.map((f) => ({
                  farm:           f["intake__pool__ghse__farm__name"],
                  total_qc:       f.total_qc,
                  total_nets:     f.total_nets,
                  quarantine_nets:f.quarantine_nets,
                  rejected_nets:  f.rejected_nets,
                })), ["farm","total_qc","total_nets","quarantine_nets","rejected_nets"])}
              >
                ↓ CSV
              </button>
            </div>
          </div>
          <div className="card">
            <div className="card-body">
              <div className="farm-breakdown-list">
                {farmBreakdown.map((farm, i) => {
                  const total      = farm.total_nets || 1;
                  const approvedW  = ((total - farm.quarantine_nets - farm.rejected_nets) / total) * 100;
                  const quarantineW= (farm.quarantine_nets / total) * 100;
                  const rejectedW  = (farm.rejected_nets  / total) * 100;
                  return (
                    <div className="farm-row" key={i}>
                      <div className="farm-header">
                        <span className="farm-name">🌾 {farm["intake__pool__ghse__farm__name"] || "Unknown"}</span>
                        <div className="farm-stats">
                          <span className="farm-stat">Total: <span>{fmt(farm.total_nets)}</span></span>
                          <span className="farm-stat">QC: <span>{fmt(farm.total_qc)}</span></span>
                          <span className="farm-stat" style={{ color: "#a96b10" }}>Quar: <span>{fmt(farm.quarantine_nets)}</span></span>
                          <span className="farm-stat" style={{ color: "#b33030" }}>Rej: <span>{fmt(farm.rejected_nets)}</span></span>
                        </div>
                      </div>
                      <div className="farm-multi-bar">
                        <div className="farm-bar-seg" style={{ width: `${approvedW}%`,  background: "#40b074" }} />
                        <div className="farm-bar-seg" style={{ width: `${quarantineW}%`,background: "#e89020" }} />
                        <div className="farm-bar-seg" style={{ width: `${rejectedW}%`,  background: "#d95050" }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </>
      )}

      <div className="section-label"><h2>Quick Insights</h2></div>
      <div className="insights-strip">
        {farmBreakdown.slice(0, 1).map((farm, i) => (
          <React.Fragment key={i}>
            <div className="insight-item"><div className="insight-key">Top Farm</div><div className="insight-val olive">{farm["intake__pool__ghse__farm__name"] || "—"}</div></div>
            <div className="insight-item"><div className="insight-key">QC Checks</div><div className="insight-val">{fmt(farm.total_qc)}</div></div>
            <div className="insight-item"><div className="insight-key">Quarantine Nets</div><div className="insight-val">{fmt(farm.quarantine_nets)}</div></div>
            <div className="insight-item"><div className="insight-key">Rejected Nets</div><div className="insight-val">{fmt(farm.rejected_nets)}</div></div>
          </React.Fragment>
        ))}
        <div className="insight-item"><div className="insight-key">Approval Rate</div><div className="insight-val olive">{fmtPct(rates.approvalRate)}</div></div>
        <div className="insight-item"><div className="insight-key">Rejection Rate</div><div className="insight-val">{fmtPct(rates.rejectionRate)}</div></div>
        <div className="insight-item"><div className="insight-key">Quarantine Rate</div><div className="insight-val">{fmtPct(rates.quarantineRate)}</div></div>
      </div>
    </>
  );
};

// ─────────────────────────────────────────────
// QC RECORDS TAB  (client-side filters only;
//  date filtering is now done server-side)
// ─────────────────────────────────────────────
const QCRecordsTab = ({ qcRecords, counts }) => {
  const [statusFilter,  setStatusFilter]  = useState("all");
  const [farmFilter,    setFarmFilter]    = useState("all");
  const [varietyFilter, setVarietyFilter] = useState("all");
  const [splitFilter,   setSplitFilter]   = useState("all");
  const [pdFilter,      setPdFilter]      = useState("all");
  const [cutFilter,     setCutFilter]     = useState("all");
  const [hooksFilter,   setHooksFilter]   = useState("all");
  const [arrFilter,     setArrFilter]     = useState("all");
  const [fullyQCed,     setFullyQCed]     = useState("all");
  const [searchId,      setSearchId]      = useState("");
  const [sortCol,       setSortCol]       = useState("id");
  const [sortDir,       setSortDir]       = useState("desc");

  const farms     = useMemo(() => [...new Set(qcRecords.map((r) => r.intake_details?.pool?.farm_name).filter(Boolean))],    [qcRecords]);
  const varieties = useMemo(() => [...new Set(qcRecords.map((r) => r.intake_details?.pool?.variety_name).filter(Boolean))], [qcRecords]);

  const filtered = useMemo(() => {
    let recs = [...qcRecords];
    if (statusFilter  !== "all") recs = recs.filter((r) => r.status === statusFilter);
    if (farmFilter    !== "all") recs = recs.filter((r) => r.intake_details?.pool?.farm_name    === farmFilter);
    if (varietyFilter !== "all") recs = recs.filter((r) => r.intake_details?.pool?.variety_name === varietyFilter);
    if (splitFilter === "parent") recs = recs.filter((r) => r.has_split_children);
    if (splitFilter === "child")  recs = recs.filter((r) => r.is_split_child);
    if (splitFilter === "none")   recs = recs.filter((r) => !r.is_split_child && !r.has_split_children);
    if (pdFilter    !== "all") recs = recs.filter((r) => String(r.pest_disease_ok) === pdFilter);
    if (cutFilter   !== "all") recs = recs.filter((r) => String(r.cut_stage_ok)    === cutFilter);
    if (hooksFilter !== "all") recs = recs.filter((r) => String(r.hooks_ok)        === hooksFilter);
    if (arrFilter   !== "all") recs = recs.filter((r) => String(r.arrangement_ok)  === arrFilter);
    if (fullyQCed   !== "all") recs = recs.filter((r) => fullyQCed === "yes" ? r.qc_progress?.is_fully_qced : !r.qc_progress?.is_fully_qced);
    if (searchId) recs = recs.filter((r) => String(r.id).includes(searchId) || String(r.intake).includes(searchId));

    recs.sort((a, b) => {
      let va = a[sortCol], vb = b[sortCol];
      if (sortCol === "farm")    { va = a.intake_details?.pool?.farm_name;    vb = b.intake_details?.pool?.farm_name; }
      if (sortCol === "variety") { va = a.intake_details?.pool?.variety_name; vb = b.intake_details?.pool?.variety_name; }
      if (typeof va === "string") return sortDir === "asc" ? va.localeCompare(vb) : vb.localeCompare(va);
      return sortDir === "asc" ? va - vb : vb - va;
    });
    return recs;
  }, [qcRecords, statusFilter, farmFilter, varietyFilter, splitFilter, pdFilter, cutFilter, hooksFilter, arrFilter, fullyQCed, searchId, sortCol, sortDir]);

  const handleSort = (col) => {
    if (sortCol === col) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortCol(col); setSortDir("asc"); }
  };
  const sortIcon = (col) => sortCol === col ? (sortDir === "asc" ? " ↑" : " ↓") : "";

  const clearFilters = () => {
    setStatusFilter("all"); setFarmFilter("all"); setVarietyFilter("all");
    setSplitFilter("all"); setPdFilter("all"); setCutFilter("all");
    setHooksFilter("all"); setArrFilter("all"); setFullyQCed("all");
    setSearchId("");
  };

  const FilterSel = ({ label, value, onChange, opts }) => (
    <div className="filter-group">
      <span className="filter-label">{label}</span>
      <select className="filter-select" value={value} onChange={(e) => onChange(e.target.value)}>
        {opts.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </div>
  );

  return (
    <div className="card" style={{ marginTop: 24 }}>
      <div className="card-header">
        <div className="sub-tabs">
          {[
            ["all",        `All (${qcRecords.length})`],
            ["APPROVED",   `Approved (${counts.approved   || 0})`],
            ["REJECTED",   `Rejected (${counts.rejected   || 0})`],
            ["QUARANTINE", `Quarantine (${counts.quarantine || 0})`],
          ].map(([k, l]) => (
            <button key={k} className={`sub-tab ${statusFilter === k ? "active" : ""}`} onClick={() => setStatusFilter(k)}>{l}</button>
          ))}
        </div>
        <button className="btn-csv" onClick={() => exportQCRecordsCSV(filtered)}>↓ Export {filtered.length} records</button>
      </div>

      <div className="filters-bar">
        <input
          type="text" className="filter-input" placeholder="Search ID / Intake…"
          value={searchId} onChange={(e) => setSearchId(e.target.value)}
        />
        <FilterSel label="Farm"       value={farmFilter}    onChange={setFarmFilter}    opts={[["all","All Farms"],     ...farms.map((f) => [f, f])]} />
        <FilterSel label="Variety"    value={varietyFilter} onChange={setVarietyFilter} opts={[["all","All Varieties"], ...varieties.map((v) => [v, v])]} />
        <FilterSel label="Split"      value={splitFilter}   onChange={setSplitFilter}   opts={[["all","All"],["parent","Has Children"],["child","Is Child"],["none","No Split"]]} />
        <FilterSel label="Fully QC'd" value={fullyQCed}     onChange={setFullyQCed}     opts={[["all","All"],["yes","Yes"],["no","No"]]} />
        <FilterSel label="P&D"        value={pdFilter}      onChange={setPdFilter}      opts={[["all","All"],["true","Pass"],["false","Fail"]]} />
        <FilterSel label="Cut Stage"  value={cutFilter}     onChange={setCutFilter}     opts={[["all","All"],["true","Pass"],["false","Fail"]]} />
        <FilterSel label="Hooks"      value={hooksFilter}   onChange={setHooksFilter}   opts={[["all","All"],["true","Pass"],["false","Fail"]]} />
        <FilterSel label="Arrangement"value={arrFilter}     onChange={setArrFilter}     opts={[["all","All"],["true","Pass"],["false","Fail"]]} />
        <button className="btn-clear-filters" onClick={clearFilters}>✕ Clear</button>
      </div>

      <div style={{ padding: "8px 20px", borderBottom: "1px solid var(--border-light)" }}>
        <span className="results-count">
          Showing <strong>{Math.min(filtered.length, 50)}</strong> of <strong>{filtered.length}</strong> records
        </span>
      </div>

      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              {[
                ["id","ID"], ["status","Status"], ["farm","Farm"], ["variety","Variety"],
                ["qc_nets","QC Nets"], ["available_nets","Available"],
              ].map(([col, label]) => (
                <th key={col} className={sortCol === col ? "sorted" : ""} onClick={() => handleSort(col)}>
                  {label}{sortIcon(col)}
                </th>
              ))}
              <th>Progress</th>
              <th>P&D / CUT / HKS / ARR</th>
              <th>Checks</th>
              <th>Split</th>
              <th className={sortCol === "checked_at" ? "sorted" : ""} onClick={() => handleSort("checked_at")}>
                Checked At{sortIcon("checked_at")}
              </th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {filtered.slice(0, 50).map((rec) => <QCRecordRow key={rec.id} rec={rec} />)}
          </tbody>
        </table>
        {filtered.length === 0 && (
          <div className="empty-state"><span className="icon">🔍</span>No records match your filters</div>
        )}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────
// SPLIT TREE TAB
// ─────────────────────────────────────────────
const SplitTreeTab = ({ splitTree }) => {
  const parents = splitTree.filter((s) => s.children && s.children.length > 0);
  if (!parents.length) return <div className="empty-state"><span className="icon">🔀</span>No split trees found</div>;
  return (
    <div className="card" style={{ marginTop: 24 }}>
      <div className="card-header">
        <h3>Split QC Trees</h3>
        <button
          className="btn-csv"
          onClick={() => exportCSV("split_trees.csv", parents.map((s) => ({
            root_qc: s.root_qc, status: s.status, nets: s.nets,
            children_count: s.children.length, children_ids: s.children.join("|"),
          })), ["root_qc","status","nets","children_count","children_ids"])}
        >
          ↓ Export CSV
        </button>
      </div>
      <div className="table-wrap">
        <table>
          <thead><tr><th>Root QC</th><th>Status</th><th>Nets</th><th>Children</th></tr></thead>
          <tbody>
            {parents.map((s, i) => (
              <tr key={i}>
                <td className="td-mono" style={{ color: "#a92f6f", fontWeight: 600 }}>#{s.root_qc}</td>
                <td>{statusBadge(s.status)}</td>
                <td className="td-mono">{fmt(s.nets)}</td>
                <td>
                  <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                    {s.children.map((cid) => (
                      <span
                        key={cid}
                        style={{
                          background: "#f5dcea", borderRadius: 4, padding: "2px 8px",
                          fontSize: "0.72rem", fontFamily: "'JetBrains Mono', monospace",
                          color: "#a92f6f", fontWeight: 600,
                        }}
                      >
                        #{cid}
                      </span>
                    ))}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────
// OVERVIEW TAB
// ─────────────────────────────────────────────
const OverviewTab = ({ data }) => {
  const kpis      = data.kpis       || {};
  const counts    = data.counts     || {};
  const rates     = data.rates      || {};
  const splitTree = data.split_tree || [];
  const qcRecords = data.qc_records || [];

  const totalQC      = kpis.totalQC   || 0;
  const totalNets    = kpis.totalNets || 0;
  const qcPct        = totalNets > 0 ? Math.round((totalQC / totalNets) * 100) : 0;
  const splitParents = splitTree.filter((s) => s.children && s.children.length > 0);

  const kpiCards = [
    { cls: "rose",  icon: "📋", val: fmt(totalQC),             label: "Total QC Checks",     sub: `${qcPct}% coverage of ${fmt(totalNets)} nets`, pct: qcPct },
    { cls: "green", icon: "✅", val: fmt(kpis.approvedNets),   label: "Approved Nets",        sub: `${fmt(counts.approved)} check records`,        pct: rates.approvalRate   || 0 },
    { cls: "red",   icon: "❌", val: fmt(kpis.rejectedNets),   label: "Rejected Nets",        sub: `${fmt(counts.rejected)} check records`,        pct: rates.rejectionRate  || 0 },
    { cls: "amber", icon: "🧪", val: fmt(kpis.quarantineNets), label: "Quarantine Nets",      sub: `${fmt(counts.quarantine)} check records`,      pct: rates.quarantineRate || 0 },
    { cls: "olive", icon: "🌱", val: fmt(totalNets),           label: "Total Nets in System", sub: `${qcPct}% QC coverage`,                        pct: qcPct },
    { cls: "slate", icon: "🔀", val: splitParents.length,      label: "Split QC Trees",       sub: `${qcRecords.filter((r) => r.is_split_child).length} leaf resolutions` },
  ];

  return (
    <>
      <div className="kpi-grid" style={{ marginTop: 24 }}>
        {kpiCards.map((k, i) => (
          <div className={`kpi-card ${k.cls}`} key={i}>
            <div className="kpi-icon">{k.icon}</div>
            <div className="kpi-value">{k.val}</div>
            <div className="kpi-label">{k.label}</div>
            <div className="kpi-sub">{k.sub}</div>
            {k.pct !== undefined && (
              <div className="progress-bar-wrap">
                <div className="progress-bar-fill" style={{ width: `${k.pct}%` }} />
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="section-label" style={{ marginTop: 32 }}>
        <h2>Check Area Summary</h2>
        <span className="pill">Selected Period</span>
      </div>
      <div className="twin-col">
        {[
          { key: "pest_disease_ok", label: "Pest & Disease", icon: "🌿" },
          { key: "cut_stage_ok",    label: "Cut Stage",      icon: "✂️" },
          { key: "hooks_ok",        label: "Hooks",          icon: "🪝" },
          { key: "arrangement_ok",  label: "Arrangement",    icon: "📐" },
        ].map((check) => {
          const total  = qcRecords.length;
          const passed = qcRecords.filter((r) => r[check.key]).length;
          const failed = total - passed;
          const pct    = total > 0 ? Math.round((passed / total) * 100) : 0;
          return (
            <div className="card" key={check.key}>
              <div className="card-body">
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 10 }}>
                  <span style={{ fontWeight: 700, fontSize: "0.9rem" }}>{check.icon} {check.label}</span>
                  <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "0.85rem", fontWeight: 700, color: "#a92f6f" }}>{pct}%</span>
                </div>
                <div className="progress-bar-wrap" style={{ height: 8, marginBottom: 12 }}>
                  <div
                    className="progress-bar-fill"
                    style={{ width: `${pct}%`, background: pct > 70 ? "#40b074" : pct > 40 ? "#e89020" : "#d95050" }}
                  />
                </div>
                <div style={{ display: "flex", gap: 12 }}>
                  <span style={{ fontSize: "0.78rem", color: "#2d7d4f", fontWeight: 600 }}>✓ {fmt(passed)} pass</span>
                  <span style={{ fontSize: "0.78rem", color: "#b33030", fontWeight: 600 }}>✗ {fmt(failed)} fail</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
};

// ─────────────────────────────────────────────
// MAIN DASHBOARD
// ─────────────────────────────────────────────
const IntakeQCDashboardPage = () => {
  // Default to last 7 days on mount
  const [dateRange, setDateRange] = useState(getDefault7Days);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);
  const [data, setData]           = useState(null);
  const [activeTab, setActiveTab] = useState("overview");

  const fetchData = useCallback(async (range) => {
    try {
      setLoading(true);
      setError(null);
      const result = await fetchQCDashboard({
        startDate: toISODate(range?.start),
        endDate:   toISODate(range?.end),
      });
      if (!result) throw new Error("No data returned from API");
      setData(result);
    } catch (err) {
      setError(err.message || "Failed to load dashboard");
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch on mount with default range
  useEffect(() => { fetchData(dateRange); }, [fetchData]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDateRangeChange = useCallback((range) => {
    setDateRange(range);
    fetchData(range);
  }, [fetchData]);

  if (loading) return (
    <div className="state-screen">
      <div className="spinner" />
      <h3>Loading Dashboard</h3>
      <p>Fetching QC records…</p>
    </div>
  );

  if (error) return (
    <div className="state-screen">
      <span style={{ fontSize: "2.5rem" }}>🌿</span>
      <h3>Couldn't load data</h3>
      <p style={{ color: "#b33030" }}>{error}</p>
      <button className="btn-refresh" style={{ background: "#a92f6f" }} onClick={() => fetchData(dateRange)}>↺ Retry</button>
    </div>
  );

  if (!data) return null;

  const kpis          = data.kpis            || {};
  const counts        = data.counts          || {};
  const rates         = data.rates           || {};
  const qcRecords     = data.qc_records      || [];
  const splitTree     = data.split_tree      || [];
  const poolSnapshot  = data.pool_snapshot   || [];
  const intakeSnapshot= data.intake_snapshot || [];
  const meta          = data.meta            || {};

  const totalNets    = kpis.totalNets || 0;
  const totalQC      = kpis.totalQC   || 0;
  const qcPct        = totalNets > 0 ? Math.round((totalQC / totalNets) * 100) : 0;
  const splitParents = splitTree.filter((s) => s.children && s.children.length > 0);
  const uniquePools  = new Set((poolSnapshot || []).map((r) => r["intake__pool__id"])).size;
  const totalComments= qcRecords.reduce(
    (n, r) => n + CHECK_FIELDS.filter((f) => r[f.key]?.trim()).length, 0
  );

  const navItems = [
    { key: "overview",  label: "Overview",      count: null },
    { key: "records",   label: "QC Records",    count: qcRecords.length },
    { key: "analytics", label: "Analytics",     count: null },
    { key: "comments",  label: "Comments",      count: totalComments },
    { key: "pools",     label: "Pool Snapshot", count: uniquePools },
    { key: "intakes",   label: "Intakes",       count: new Set((intakeSnapshot || []).map((r) => r["intake__id"])).size },
    { key: "splits",    label: "Split Trees",   count: splitParents.length },
  ];

  return (
    <div className="dashboard-root fade-in">
      <div className="dash-header">
        <div className="dash-header-left">
          <h1>🌿 Intake <span className="accent">QC</span> Dashboard</h1>
          <p>{meta.note || "Quality Control — Date Range View"}</p>
        </div>
        <div className="dash-header-right">
          {/* ── Date range picker ── */}
          <div className="dash-date-filter">
            <span className="dash-date-label">Period</span>
            <DateRangeFilter value={dateRange} onChange={handleDateRangeChange} />
          </div>
          <span className="mode-badge">{meta.mode || "DASHBOARD"}</span>
          <button className="btn-export-all" onClick={() => exportQCRecordsCSV(qcRecords, "all_qc_records.csv")}>↓ Export All</button>
          <button className="btn-refresh" onClick={() => fetchData(dateRange)}>↺ Refresh</button>
        </div>
      </div>

      <div className="nav-tabs">
        {navItems.map((t) => (
          <button
            key={t.key}
            className={`nav-tab ${activeTab === t.key ? "active" : ""}`}
            onClick={() => setActiveTab(t.key)}
          >
            {t.label}
            {t.count !== null && <span className="tab-count">{t.count}</span>}
          </button>
        ))}
      </div>

      <div className="dash-content">
        <div className="meta-strip">
          {[
            { label: "Period",        val: meta.start_date && meta.end_date ? `${meta.start_date} → ${meta.end_date}` : "Last 7 days", sub: "active filter" },
            { label: "Total Records", val: meta.total_records || qcRecords.length, sub: "QC checks" },
            { label: "QC Coverage",   val: `${qcPct}%`,               sub: `${fmt(totalQC)} / ${fmt(totalNets)} nets` },
            { label: "Approval Rate", val: fmtPct(rates.approvalRate), sub: `${fmt(counts.approved)} records` },
            { label: "Unique Pools",  val: uniquePools,                sub: "in snapshot" },
            { label: "Comments",      val: totalComments,              sub: "across all checks" },
          ].map((m) => (
            <div className="meta-item" key={m.label}>
              <div className="meta-label">{m.label}</div>
              <div className="meta-val">{m.val}</div>
              <div className="meta-sub">{m.sub}</div>
            </div>
          ))}
        </div>

        {activeTab === "overview"  && <OverviewTab data={data} />}
        {activeTab === "analytics" && <AnalyticsTab data={data} />}
        {activeTab === "records"   && <QCRecordsTab qcRecords={qcRecords} counts={counts} />}
        {activeTab === "comments"  && (
          <>
            <div className="section-label" style={{ marginTop: 24 }}>
              <h2>Comment Intelligence</h2>
              <span className="pill">Word Cloud + Analytics</span>
            </div>
            <CommentsTab qcRecords={qcRecords} />
          </>
        )}
        {activeTab === "pools" && (
          <>
            <div className="section-label" style={{ marginTop: 24 }}>
              <h2>Pool Snapshot</h2>
              <span className="pill olive">Deduplicated</span>
            </div>
            <div className="card"><PoolSnapshotTable data={poolSnapshot} /></div>
          </>
        )}
        {activeTab === "intakes" && (
          <>
            <div className="section-label" style={{ marginTop: 24 }}>
              <h2>Intake Snapshot</h2>
              <span className="pill">Selected Period</span>
            </div>
            <div className="card"><IntakeSnapshotTable data={intakeSnapshot} /></div>
          </>
        )}
        {activeTab === "splits" && <SplitTreeTab splitTree={splitTree} />}
      </div>
    </div>
  );
};

export default IntakeQCDashboardPage;